import React, { useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import NotesWidget from "../../api/NotesWidget";
import { BtnPrimaryv4 } from "../UI/button/BtnPrimaryv4";
import { CcV4Toggle } from "../cc-v4/CcV4Toggle";
import { formatRemindAtForInput } from "../../utils/notesReminderUtils";

const PANEL_MIN_WIDTH = 280;
const PANEL_MIN_HEIGHT = 200;
const PANEL_DEFAULT_WIDTH = 360;
const PANEL_DEFAULT_HEIGHT = 420;

function clampSize(width, height) {
  const maxW = Math.max(PANEL_MIN_WIDTH, window.innerWidth - 16);
  const maxH = Math.max(
    PANEL_MIN_HEIGHT,
    Math.min(window.innerHeight * 0.72, 520),
  );
  return {
    width: Math.min(Math.max(PANEL_MIN_WIDTH, width), maxW),
    height: Math.min(Math.max(PANEL_MIN_HEIGHT, height), maxH),
  };
}

function clampPosition(x, y, panelWidth, panelHeight) {
  const maxX = Math.max(8, window.innerWidth - panelWidth - 8);
  const maxY = Math.max(8, window.innerHeight - panelHeight - 8);
  return {
    x: Math.min(Math.max(8, x), maxX),
    y: Math.min(Math.max(8, y), maxY),
  };
}

function NotesEmptyState() {
  return (
    <div className="my-notes-widget__empty">
      <span className="my-notes-widget__empty-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="40" height="40" fill="none">
          <path
            d="M8 3h8a2 2 0 0 1 2 2v14l-4-2.5L10 19l-4 2.5V5a2 2 0 0 1 2-2z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path
            d="M9 8h6M9 12h5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </span>
      <p className="my-notes-widget__empty-title">Пока пусто</p>
      <p className="my-notes-widget__empty-hint">
        Нажмите «Создать заметку» внизу панели
      </p>
    </div>
  );
}

const MyNotesWidget = observer(() => {
  const panelRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState(PANEL_DEFAULT_HEIGHT);

  const { enabled, open, position, panelSize, notes, highlightedNoteId } =
    NotesWidget;

  const panelWidth = panelSize?.width ?? PANEL_DEFAULT_WIDTH;

  useEffect(() => {
    if (!open || !panelRef.current) {
      return;
    }
    const el = panelRef.current;
    const ro = new ResizeObserver(() => {
      setPanelHeight(el.offsetHeight || PANEL_DEFAULT_HEIGHT);
    });
    ro.observe(el);
    setPanelHeight(el.offsetHeight || PANEL_DEFAULT_HEIGHT);
    return () => ro.disconnect();
  }, [open, notes.length, panelSize.width, panelSize.height]);

  useEffect(() => {
    if (!highlightedNoteId || !open) {
      return;
    }
    const node = document.getElementById(
      `my-notes-item-${highlightedNoteId}`,
    );
    if (node) {
      node.scrollIntoView({ block: "nearest", behavior: "smooth" });
      node.focus({ preventScroll: true });
    }
    const t = setTimeout(() => NotesWidget.clearHighlight(), 4000);
    return () => clearTimeout(t);
  }, [highlightedNoteId, open, notes.length]);

  const onPointerDownDrag = useCallback(
    (e) => {
      if (e.button !== 0) {
        return;
      }
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      const origin = { ...NotesWidget.position };

      const onMove = (ev) => {
        const next = clampPosition(
          origin.x + ev.clientX - startX,
          origin.y + ev.clientY - startY,
          panelWidth,
          panelHeight,
        );
        NotesWidget.setPosition(next);
      };

      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [panelHeight, panelWidth],
  );

  const onPointerDownResize = useCallback(
    (e) => {
      if (e.button !== 0) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startY = e.clientY;
      const originW = panelSize.width;
      const originH = panelSize.height;

      const onMove = (ev) => {
        const next = clampSize(
          originW + (ev.clientX - startX),
          originH + (ev.clientY - startY),
        );
        NotesWidget.setPanelSize(next);
      };

      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [panelSize.height, panelSize.width],
  );

  if (!enabled || !open) {
    return null;
  }

  const size = clampSize(
    panelSize.width ?? PANEL_DEFAULT_WIDTH,
    panelSize.height ?? PANEL_DEFAULT_HEIGHT,
  );
  const clamped = clampPosition(
    position.x,
    position.y,
    size.width,
    size.height,
  );

  return (
    <div
      ref={panelRef}
      className="my-notes-widget"
      style={{
        left: clamped.x,
        top: clamped.y,
        width: size.width,
        height: size.height,
        maxHeight: "min(72vh, 520px)",
      }}
      role="dialog"
      aria-label="Мои заметки"
    >
      <div
        className="my-notes-widget__header"
        onPointerDown={onPointerDownDrag}
      >
        <span className="my-notes-widget__grip" aria-hidden="true">
          <svg viewBox="0 0 10 16" width="10" height="16">
            <circle cx="2" cy="2" r="1.25" fill="currentColor" />
            <circle cx="8" cy="2" r="1.25" fill="currentColor" />
            <circle cx="2" cy="8" r="1.25" fill="currentColor" />
            <circle cx="8" cy="8" r="1.25" fill="currentColor" />
            <circle cx="2" cy="14" r="1.25" fill="currentColor" />
            <circle cx="8" cy="14" r="1.25" fill="currentColor" />
          </svg>
        </span>
        <span className="my-notes-widget__title">Мои заметки</span>
        {notes.length > 0 ? (
          <span className="my-notes-widget__count">{notes.length}</span>
        ) : null}
        <button
          type="button"
          className="my-notes-widget__close"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => NotesWidget.setOpen(false)}
          aria-label="Закрыть"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className="my-notes-widget__scroll">
        {notes.length === 0 ? (
          <NotesEmptyState />
        ) : (
          <ul className="my-notes-widget__list">
            {notes.map((note, index) => {
              const highlighted = highlightedNoteId === note.id;
              const showRemindBlock =
                note.reminderEnabled === true || note.remindAt != null;
              return (
                <li
                  key={note.id}
                  id={`my-notes-item-${note.id}`}
                  className={`my-notes-widget__item${highlighted ? " my-notes-widget__item--highlight" : ""}`}
                  tabIndex={highlighted ? -1 : undefined}
                >
                  <div className="my-notes-widget__note-card">
                    <span className="my-notes-widget__note-label">
                      Заметка {index + 1}
                    </span>
                    <textarea
                      className="my-notes-widget__textarea"
                      value={note.text}
                      placeholder="Введите текст…"
                      onPointerDown={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        NotesWidget.updateNoteText(note.id, e.target.value)
                      }
                    />
                    {showRemindBlock ? (
                      <div className="my-notes-widget__remind-block">
                        <div className="my-notes-widget__remind-row">
                          <span className="site-form-label my-notes-widget__remind-title">
                            Напоминание
                          </span>
                          <CcV4Toggle
                            active={note.reminderEnabled === true}
                            onClick={() =>
                              NotesWidget.updateNoteReminderEnabled(
                                note.id,
                                !note.reminderEnabled,
                              )
                            }
                          />
                        </div>
                        {note.reminderEnabled ? (
                          <>
                            <input
                              type="datetime-local"
                              className="my-notes-widget__remind-input site-form-control calendarV1"
                              value={formatRemindAtForInput(note.remindAt)}
                              onPointerDown={(e) => e.stopPropagation()}
                              onChange={(e) =>
                                NotesWidget.updateNoteRemindAt(
                                  note.id,
                                  e.target.value,
                                )
                              }
                            />
                            <p className="my-notes-widget__remind-hint">
                              Пока открыт личный кабинет в браузере
                            </p>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="my-notes-widget__note-actions">
                      {!showRemindBlock ? (
                        <button
                          type="button"
                          className="my-notes-widget__remind-add"
                          onClick={() =>
                            NotesWidget.updateNoteReminderEnabled(note.id, true)
                          }
                        >
                          Напоминание
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="my-notes-widget__delete"
                        onClick={() => NotesWidget.removeNote(note.id)}
                      >
                        Удалить
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <footer className="my-notes-widget__footer">
        <BtnPrimaryv4
          footerSize
          text="Создать заметку"
          onClick={() => NotesWidget.addNote()}
        />
      </footer>

      <button
        type="button"
        className="my-notes-widget__resize-handle"
        aria-label="Изменить размер панели"
        onPointerDown={onPointerDownResize}
      />
    </div>
  );
});

export { MyNotesWidget };
